import {
  ownerFactNeedsConfirmation,
  type OwnerFact,
  type OwnerFactCategory,
} from '@secretario/shared';

/**
 * O que o Secretário sabe da pessoa ("Sobre você" e o Caderno do palco): as
 * áreas, a origem de cada lembrança em linguagem simples e as contas da tela.
 * Só regras puras aqui; as chamadas ao servidor ficam em `useOwnerFacts.ts`.
 */
export type FactArea = {
  id: OwnerFactCategory;
  label: string;
  tint: string;
  labelHint: string;
  valueHint: string;
};

export const FACT_AREAS: FactArea[] = [
  {
    id: 'voce',
    label: 'Você',
    tint: '#FF7A4D',
    labelHint: 'Ex.: Profissão',
    valueHint: 'Ex.: Arquiteta',
  },
  {
    id: 'familia',
    label: 'Família',
    tint: '#E11D48',
    labelHint: 'Ex.: Aniversário da mãe',
    valueHint: 'Ex.: 12 de maio',
  },
  {
    id: 'casa',
    label: 'Casa',
    tint: '#16A34A',
    labelHint: 'Ex.: Cidade',
    valueHint: 'Ex.: Curitiba',
  },
  {
    id: 'carro',
    label: 'Carro',
    tint: '#0891B2',
    labelHint: 'Ex.: Seu carro',
    valueHint: 'Ex.: Corolla 2024',
  },
  {
    id: 'trabalho',
    label: 'Trabalho',
    tint: '#8B5CF6',
    labelHint: 'Ex.: Horário',
    valueHint: 'Ex.: 9h às 18h',
  },
  {
    id: 'gostos',
    label: 'Gostos',
    tint: '#D97706',
    labelHint: 'Ex.: Café',
    valueHint: 'Ex.: Sem açúcar',
  },
];

// Começo de frase: aqui o dono completa direto na categoria certa. Quando a
// conversa com o Secretário chegar, a mesma sugestão vira fala e ele anota sozinho.
export const FACT_SUGGESTIONS: { text: string; category: OwnerFactCategory; label: string }[] = [
  { text: 'Moro em ', category: 'casa', label: 'Cidade' },
  { text: 'Meu carro é um ', category: 'carro', label: 'Meu carro' },
  { text: 'O aniversário da minha mãe é dia ', category: 'familia', label: 'Aniversário da mãe' },
  { text: 'Trabalho das ', category: 'trabalho', label: 'Horário de trabalho' },
];

export const FACTS_KEY = ['owner-facts'] as const;

type FactOrigin = Pick<OwnerFact, 'source' | 'confirmedAt'>;

/** A lembrança foi anotada pelo Secretário e ainda espera o sim da pessoa. */
export const factNeedsConfirmation = ownerFactNeedsConfirmation;

/** De onde veio a lembrança, do jeito que o Secretário diria. */
export function factOrigin(fact: FactOrigin): string {
  if (fact.source === 'owner') return 'Você me contou';
  return fact.confirmedAt ? 'Percebi numa conversa e você confirmou' : 'Percebi numa conversa';
}

export function pendingFacts<T extends FactOrigin>(facts: T[]): T[] {
  return facts.filter(factNeedsConfirmation);
}

/** As seis áreas, na ordem de sempre, cada uma com as suas lembranças. */
export function factsByArea(facts: OwnerFact[]): { area: FactArea; facts: OwnerFact[] }[] {
  return FACT_AREAS.map((area) => ({
    area,
    facts: facts.filter((fact) => fact.category === area.id),
  }));
}

/** Título do Caderno: primeiro o que espera um sim, depois quanto ele já guarda. */
export function notebookTitle(facts: FactOrigin[]): string {
  if (facts.length === 0) return 'Ainda estou te conhecendo';
  const pending = pendingFacts(facts).length;
  if (pending === 1) return '1 lembrança para você conferir';
  if (pending > 1) return `${pending} lembranças para você conferir`;
  return facts.length === 1 ? '1 lembrança guardada' : `${facts.length} lembranças guardadas`;
}
